/**
 * @fileoverview Schedule-follow-up mutation hook.
 * @description Wraps PATCH /sales/targets/:id/follow-up in a TanStack mutation. Invalidates both
 * the owning tenant's target query and the broad targets list key — unlike useUpdateTargetStage,
 * this one also needs to refresh outer list queries (e.g. My Page's own "Next Follow-up" column),
 * same as useReassignTarget/useConfirmTargetPayment already do for their own list-visible fields.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TARGETS_QUERY_KEY } from '@/features/sales/hooks/useTargets'
import { scheduleFollowUp } from '@/features/sales/services/targets.service'

import type { ScheduleFollowUpRequest } from '@/features/sales/services/targets.service'

/**
 * @description Records a target's next planned follow-up and refreshes the affected queries.
 * @param {string} tenantId - The tenant the target belongs to, for query invalidation.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof scheduleFollowUp>>, unknown, {
 * targetId: string; input: ScheduleFollowUpRequest }>>} The mutation object (mutate with `{
 * targetId, input }`, isPending, ...).
 */
export function useScheduleFollowUp(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ targetId, input }: { targetId: string; input: ScheduleFollowUpRequest }) =>
      scheduleFollowUp(targetId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...TARGETS_QUERY_KEY, 'byTenant', tenantId] })
      void queryClient.invalidateQueries({ queryKey: TARGETS_QUERY_KEY })
    },
  })
}
